import React, { useState, useEffect } from 'react';

import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import ProductSelect from '@components/shared/products/ProductSelect';
import useSmartBack from '@hooks/shared/useSmartBack';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import Can from '@shared/permissions/Can';
import PageHeader from '@shared/PageHeader';
import { Form, Input, Button, message, Switch, Table, InputNumber, Select, Row, Col, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

interface InventoryType {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface BundleService {
  get: (id: string) => Promise<{ success: boolean; data?: any; message?: string }>;
  create: (payload: any) => Promise<{ success: boolean; data?: any; message?: string }>;
  update: (id: string, payload: any) => Promise<{ success: boolean; data?: any; message?: string }>;
  [key: string]: any;
}

interface ProductService {
  [key: string]: any;
}

interface BundleProductOption {
  value: string;
  label: string;
  sku?: string;
  name?: string;
  [key: string]: unknown;
}

interface BundleProductRow {
  key: number;
  product_id: string | null;
  product_name?: string;
  product_sku?: string;
  quantity: number;
  inventory_type_id: string | null;
  inventory_type_name?: string;
  product_option?: BundleProductOption;
}

interface BundleLoadedPayload {
  merchant_id?: string | null;
  merchant?: { id?: string | null; name?: string | null } | null;
  [key: string]: unknown;
}

interface BundleEditViewProps {
  bundleService: BundleService;
  productService: ProductService;
  inventoryTypes?: InventoryType[];
  role?: 'admin' | 'operator';
  // The merchant this form is operating on.
  //   - NEW (operator): the merchant chosen via <MerchantSelect> / OperatorDataContext.
  //   - NEW (admin): admin session is already merchant-locked; this can be omitted.
  //   - EDIT: the record's merchant_id (wrapper feeds it via useRecordMerchantSync ⇒
  //     currentMerchant has been synced to the record before this prop is read).
  // Used both as the create-payload `merchant_id` (NEW only) and as the
  // ProductSelect / reference-data filter (NEW & EDIT).
  merchantId?: string | null;
  // Called by `loadBundle` once the bundle is fetched. Wrapper uses this to sync
  // OperatorDataContext.currentMerchant to the record's merchant before any
  // merchant-scoped dropdown renders (canonical useRecordMerchantSync pattern).
  onBundleLoaded?: (bundle: BundleLoadedPayload) => void;
  // True while the wrapper is still switching context to the record's merchant.
  // Render-gated so the form doesn't briefly render against the previous merchant's options.
  merchantDataLoading?: boolean;
}

const BundleEditView: React.FC<BundleEditViewProps> = ({
  bundleService,
  productService,
  inventoryTypes = [],
  role = 'admin',
  merchantId = null,
  onBundleLoaded,
  merchantDataLoading = false,
}) => {
  const { id } = useParams();
  const goBack = useSmartBack() as () => void;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const isNew = !id;

  const [bundleProducts, setBundleProducts] = useState<BundleProductRow[]>([]);

  useEffect(() => {
    if (id) {
      loadBundle();
    }
  }, [id]);

  const loadBundle = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await bundleService.get(id);
      if (response.success) {
        const bundle = response.data;
        // 通知 wrapper：拿到 record 後讓它把 OperatorDataContext.currentMerchant
        // 對齊到該 record 的 merchant（由 useRecordMerchantSync 處理）。shared
        // 本身不再讀 / 寫 merchant context，保持 merchant-agnostic。
        onBundleLoaded?.(bundle);
        form.setFieldsValue({
          name: bundle.name,
          sku: bundle.sku,
          barcode: bundle.barcode,
          activated: bundle.activated ?? true,
        });
        if (bundle.bundle_products && bundle.bundle_products.length > 0) {
          setBundleProducts(bundle.bundle_products.map((bp: any, index: number) => ({
            key: index,
            product_id: bp.product_id,
            product_name: bp.product?.name,
            product_sku: bp.product?.sku,
            quantity: bp.quantity,
            inventory_type_id: bp.inventory_type_id,
            inventory_type_name: bp.inventory_type?.name,
            product_option: {
              value: bp.product_id,
              label: `${bp.product?.sku} - ${bp.product?.name}`,
              sku: bp.product?.sku,
              name: bp.product?.name
            }
          })));
        }
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.load_failed') });
      }
    } catch (error) {
      console.error('Load bundle error:', error);
      handleNetworkError(error, { t });
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = () => {
    const newKey = bundleProducts.length > 0
      ? Math.max(...bundleProducts.map(bp => bp.key)) + 1
      : 0;
    setBundleProducts([...bundleProducts, {
      key: newKey,
      product_id: null,
      product_name: '',
      product_sku: '',
      quantity: 1,
      inventory_type_id: null
    }]);
  };

  const handleRemoveProduct = (key: number) => {
    setBundleProducts(bundleProducts.filter(bp => bp.key !== key));
  };

  const handleProductSelect = (key: number, productId: string, option?: BundleProductOption) => {
    setBundleProducts(bundleProducts.map(bp =>
      bp.key === key
        ? {
            ...bp,
            product_id: productId,
            product_name: option?.name,
            product_sku: option?.sku,
            product_option: option
          }
        : bp
    ));
  };

  const handleQuantityChange = (key: number, quantity: number | null) => {
    setBundleProducts(bundleProducts.map(bp =>
      bp.key === key ? { ...bp, quantity: quantity ?? 1 } : bp
    ));
  };

  const handleInventoryTypeChange = (key: number, inventory_type_id: string | null) => {
    setBundleProducts(bundleProducts.map(bp =>
      bp.key === key ? { ...bp, inventory_type_id } : bp
    ));
  };

  const validateBundleProducts = () => {
    if (bundleProducts.length === 0) {
      message.error(t(`shared.bundles.form.no_products_error`));
      return false;
    }
    const hasEmptyProduct = bundleProducts.some(bp => !bp.product_id);
    if (hasEmptyProduct) {
      message.error(t(`shared.bundles.form.incomplete_products_error`));
      return false;
    }
    return true;
  };

  const buildPayload = (values: Record<string, any>) => {
    const processedValues: Record<string, any> = {
      ...values,
      bundle_products: bundleProducts.map(bp => ({
        product_id: bp.product_id,
        quantity: bp.quantity,
        inventory_type_id: bp.inventory_type_id,
      })),
    };
    if (role === 'operator' && !id && merchantId) {
      processedValues.merchant_id = merchantId;
    }
    return processedValues;
  };

  const handleSubmit = async (values: Record<string, any>) => {
    if (!validateBundleProducts()) return;
    try {
      setLoading(true);
      const response = id
        ? await bundleService.update(id, buildPayload(values))
        : await bundleService.create(buildPayload(values));

      if (response.success) {
        message.success(t(`common.message.save_success`));
        goBack();
      } else {
        handleApiError(response, {
          t,
          fallbackMessage: id ? t('shared.bundles.form.update_error') : t('shared.bundles.form.create_error'),
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      handleNetworkError(error, { t });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      if (!validateBundleProducts()) return;

      setSavingAndAdding(true);
      try {
        const response = await bundleService.create(buildPayload(values));
        if (response.success) {
          message.success(t(`common.message.save_success`));
          form.resetFields();
          setBundleProducts([]);
          form.setFieldsValue({ activated: true });
        } else {
          handleApiError(response, { t, fallbackMessage: t('shared.bundles.form.create_error') });
        }
      } catch (error) {
        console.error('Submit error:', error);
        handleNetworkError(error, { t });
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields rejects on validation failure; antd highlights the fields
    }
  };

  const columns = [
    {
      title: t('models.product.name'),
      dataIndex: 'product_id',
      key: 'product',
      width: '40%',
      render: (product_id: string | null, record: BundleProductRow) => (
        <ProductSelect
          data-testid="bundle-product-search"
          productService={productService}
          merchantId={merchantId ?? undefined}
          value={product_id}
          defaultOptions={record.product_option ? [record.product_option] : []}
          placeholder={t('shared.bundles.form.select_product')}
          style={{ width: '100%' }}
          onSelect={(val: string, option: BundleProductOption | undefined) => handleProductSelect(record.key, val, option)}
        />
      ),
    },
    {
      title: t('models.inventory_type.name'),
      dataIndex: 'inventory_type_id',
      key: 'inventory_type',
      width: '25%',
      render: (inventory_type_id: string | null, record: BundleProductRow) => {
        const baseOptions: Array<{ value: string | null; label: string }> = [
          { value: null, label: t('shared.bundles.form.default_inventory_type') },
          ...inventoryTypes.map(type => ({
            value: type.id,
            label: type.name ?? type.id,
          }))
        ];
        if (inventory_type_id && !baseOptions.some(o => o.value === inventory_type_id) && record.inventory_type_name) {
          baseOptions.push({ value: inventory_type_id, label: record.inventory_type_name });
        }
        return (
          <Select
            showSearch
            allowClear
            value={inventory_type_id}
            placeholder={t('shared.bundles.form.default_inventory_type_placeholder')}
            style={{ width: '100%' }}
            onChange={(value) => handleInventoryTypeChange(record.key, value)}
            filterOption={(input, option) =>
              String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={baseOptions as any}
          />
        );
      },
    },
    {
      title: t('models.inventory.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: '20%',
      render: (quantity: number, record: BundleProductRow) => (
        <InputNumber
          min={1}
          value={quantity}
          onChange={(value) => handleQuantityChange(record.key, value)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('common.table.actions'),
      key: 'actions',
      width: '15%',
      render: (_: unknown, record: BundleProductRow) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveProduct(record.key)}
        >
          {t('common.button.delete')}
        </Button>
      ),
    },
  ];

  const headerBasePath = role === 'operator' ? '/operator' : '/admin';

  // Render-gate: sync 期間（loading 抓 record；merchantDataLoading 切 context 到
  // record 的 merchant）整段 form 不能 mount，避免 ProductSelect 先用 header merchant
  // 的清單發 search request。
  if (loading || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        basePath={headerBasePath}
        items={['products', 'products.bundles', { label: id ? t('common.button.edit') : t('common.button.create') }]}
      />
      <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ activated: true }}
        >
          <Form.Item
            name="name"
            label={t('models.bundle.name')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sku"
                label={t('models.bundle.sku')}
                rules={[{ required: true, message: t('common.validation.required') }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="barcode"
                label={t('models.bundle.barcode')}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="activated"
                label={t('models.bundle.activated')}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ marginBottom: 24 }}>
            <Typography.Title level={5}>
              {t('models.bundle.bundle_items')}
            </Typography.Title>
            <Table
              columns={columns}
              dataSource={bundleProducts}
              rowKey="key"
              pagination={false}
              locale={{
                emptyText: (
                  <div style={{ padding: '24px 0' }}>
                    <p style={{ color: '#999', marginBottom: 12 }}>{t('shared.bundles.detail.no_products')}</p>
                    <Button icon={<PlusOutlined />} onClick={handleAddProduct}>
                      {t('shared.bundles.form.add_product')}
                    </Button>
                  </div>
                ),
              }}
            />
          </div>
          <FloatingActionBarSpacer />
        </Form>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={loading}
        disabled={savingAndAdding}
        submitRequiredPermission="products:write"
        extraButtons={
          <>
            <Button
              icon={<PlusOutlined />}
              onClick={handleAddProduct}
            >
              {t('shared.bundles.form.add_product')}
            </Button>
            {isNew && (
              <Can requiredPermission="products:write">
                <Button
                  loading={savingAndAdding}
                  disabled={loading}
                  onClick={handleSaveAndAddAnother}
                >
                  {t('common.button.save_and_add_another')}
                </Button>
              </Can>
            )}
          </>
        }
        submitText={t(isNew ? 'common.button.create' : 'common.button.save')}
      />
    </div>
  );
};

export default BundleEditView;
